import { useState } from "react";

import { Sparkles } from "lucide-react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { useAIReview, useRunAIReview, type AIFinding } from "../api";
import { ModelPickerButton, shortName, storedModel } from "./ModelPickerButton";

export const SEVERITY_STATUS: Record<AIFinding["severity"], string> = {
  low: "pending",
  medium: "at_risk",
  high: "churned",
};

/**
 * The AI review block for one parent group. Runs a local Ollama model over
 * everything we can read about the parent + children (names, brand,
 * geography, users, email domains, account) and reports companies that look
 * miscast. Verdicts persist server-side so the grid can show flags; the model
 * pick persists in localStorage. The AI Review tab runs the same thing across
 * every parent in one go.
 */
export function AIReviewCard({
  parentId,
  parentName,
}: {
  parentId: string;
  parentName: string;
}) {
  const review = useAIReview(parentId);
  const run = useRunAIReview();
  const [model, setModel] = useState<string>(storedModel);

  const start = () => {
    run.mutate(
      { parentId, model: model || null },
      {
        onSuccess: (r) =>
          r.status === "clear"
            ? toast.success("AI review: relationships look plausible")
            : toast.warning(`AI review flagged ${r.findings.length} concern(s)`),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const data = review.data;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand" />
          <span className="text-sm font-semibold">AI relationship review</span>
          {data && <StatusBadge value={data.status} />}
          <div className="ml-auto flex items-center gap-2">
            <ModelPickerButton model={model} onModelChange={setModel} />
            <Button size="sm" className="h-7 text-xs" onClick={start} disabled={run.isPending}>
              {run.isPending ? "Reviewing…" : data ? "Re-run review" : "Run review"}
            </Button>
          </div>
        </div>

        {run.isPending && (
          <p className="text-xs text-muted-foreground">
            The local model is reading {parentName} and every linked company —
            names, brands, geography, users, email domains, and accounts. This
            can take a minute on larger models.
          </p>
        )}

        {!data && !run.isPending && (
          <p className="text-xs text-muted-foreground">
            Not reviewed yet. The review pulls each company's profile from Cinch
            and asks the local model whether anything looks linked to the wrong
            parent.
          </p>
        )}

        {data && <ReviewVerdict data={data} />}
      </CardContent>
    </Card>
  );
}

/** Summary + findings + provenance line — shared with the AI Review tab. */
export function ReviewVerdict({
  data,
}: {
  data: {
    summary: string | null;
    findings: AIFinding[];
    model: string | null;
    reviewed_at: string | null;
    reviewed_by_email: string | null;
  };
}) {
  return (
    <div className="flex flex-col gap-2">
      {data.summary && <p className="text-sm">{data.summary}</p>}
      {data.findings.length > 0 && (
        <ul className="flex flex-col gap-2">
          {data.findings.map((f, i) => (
            <li key={i} className="rounded-md border border-border p-2">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge
                  value={SEVERITY_STATUS[f.severity]}
                  label={f.severity.toUpperCase()}
                />
                <span className="text-sm font-medium">
                  {f.company_name ?? "Unnamed company"}
                </span>
              </div>
              <p className="mt-1 text-sm">{f.concern}</p>
              {f.evidence && (
                <p className="mt-0.5 text-xs text-muted-foreground">{f.evidence}</p>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        {data.model ? `Model ${shortName(data.model)}` : "Local model"}
        {data.reviewed_at ? ` · ${new Date(data.reviewed_at).toLocaleString()}` : ""}
        {data.reviewed_by_email ? ` · run by ${data.reviewed_by_email}` : ""}
        {" · advisory only — it never changes data."}
      </p>
    </div>
  );
}
